/**
 * Mini's resting position, remembered per monitor — same two layers as
 * `@shared/sidepanel/display-geometry`: where the user parked it on *this*
 * screen, falling back to where they last parked it on any screen.
 *
 * Coordinates are viewport px from the overlay's top-left, unclamped; the
 * destination display's bounds are applied when the position is read. Storing
 * a clamped value would let a trip through a smaller monitor overwrite where
 * the user actually parked Mini.
 */

export const MINI_POSITION_SETTING_KEY = 'mini.positions'

/** Legacy display-independent key, folded into `inherited` by `migrateOverlayGeometryPerDisplay`. */
export const LEGACY_MINI_POSITION_SETTING_KEY = 'mini.position'

export interface MiniPosition {
  x: number
  y: number
}

export interface MiniPositionStore {
  /** Keyed by `overlayDisplayKey`. Only monitors the user has dragged Mini on. */
  displays?: Record<string, MiniPosition>
  inherited?: MiniPosition
}

const isMiniPosition = (value: unknown): value is MiniPosition => {
  if (!value || typeof value !== 'object') {
    return false
  }
  const { x, y } = value as Partial<MiniPosition>
  return typeof x === 'number' && Number.isFinite(x) && typeof y === 'number' && Number.isFinite(y)
}

/** `null` when the user has never placed Mini anywhere — callers use their own default. */
export function resolveMiniPosition(
  store: MiniPositionStore | undefined,
  displayKey: string | null,
): MiniPosition | null {
  const own = displayKey ? store?.displays?.[displayKey] : undefined
  if (isMiniPosition(own)) {
    return own
  }
  return isMiniPosition(store?.inherited) ? store.inherited : null
}

export function applyMiniPosition(
  store: MiniPositionStore | undefined,
  displayKey: string | null,
  position: MiniPosition,
): MiniPositionStore {
  if (!isMiniPosition(position)) {
    return store ?? {}
  }
  if (!displayKey) {
    return { ...store, inherited: position }
  }
  return {
    ...store,
    displays: { ...store?.displays, [displayKey]: position },
    inherited: position,
  }
}
